import { useCallback, useEffect } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector, useModal } from '@store/hooks';

import type {
  EligibilityDetailRequest,
  LedgerDrawerProps,
  PaymentSheetRequest,
} from '../types';

import {
  closeLedgerDrawer,
  openLedgerDrawer as openLedgerDrawerAction,
} from './slice';

/**
 * Read the drawer's current props (null when closed).
 *
 * Used by `PatientLedgerDrawerHost`. Application code should usually
 * trigger via `useOpenLedgerDrawer` instead.
 */
export function useLedgerDrawerProps(): LedgerDrawerProps | null {
  return useAppSelector(
    (state) =>
      (state as { 'patientLedger/drawer'?: { props: LedgerDrawerProps | null } })[
        'patientLedger/drawer'
      ]?.props ?? null,
  );
}

/**
 * Convenience trigger for opening the Patient Ledger Drawer.
 *
 * Replaces the old patchwork of `useModal().open(MODAL_TYPES.PAYMENT_REQUEST, ...)`,
 * `MODAL_TYPES.RECORD_PAYMENT`, `MODAL_TYPES.WRITE_OFF`, `MODAL_TYPES.CAPTURE_CHARGES`,
 * and the inline LogCollectionForm — all eight legacy surfaces funnel here.
 *
 * Calling this twice doesn't stack — the second call updates the props in
 * place. That gives us "destination surface" semantics with no special
 * coordination required from triggers.
 */
export function useOpenLedgerDrawer(): (props: LedgerDrawerProps) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (props: LedgerDrawerProps) => {
      if (!props.patientId) return;
      dispatch(openLedgerDrawerAction(props));
    },
    [dispatch],
  );
}

export function useCloseLedgerDrawer(): () => void {
  const dispatch = useAppDispatch();
  return useCallback(() => {
    dispatch(closeLedgerDrawer());
  }, [dispatch]);
}

// ─── Payment sheets (gdn-2957) ───────────────────────────────────────────────
//
// The drawer's replacement for action-only entries. Goes through the app's
// modal registry rather than a bespoke host, so it inherits Dialog chrome,
// the slide-up transition, stacking and focus handling for free.

/**
 * Open a settle / add-charge sheet as an ordinary app modal.
 *
 * Same trigger ergonomics the drawer had — `useOpenPaymentSheet()(request)` —
 * so migrating a call site off `useOpenLedgerDrawer` is a rename plus a
 * flattened payload.
 */
export function useOpenPaymentSheet(): (request: PaymentSheetRequest) => void {
  const { openAsDestination } = useModal();
  const gated = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);

  // Warm the modal's chunk for users who can open it.
  //
  // The drawer was eagerly mounted in auth0-provider, so it painted the
  // instant you clicked. A registry modal is lazy, so without this the
  // Dialog opens showing ModalLoadingState ("Loading payment options…")
  // and then swaps to the sheet — which reads as the dialog opening and
  // then changing into something else. Prefetching on mount means the
  // chunk is already resident by the time anything is clicked.
  //
  // Gated so ungated users, who can never open this, don't pay for it.
  useEffect(() => {
    if (!gated) return;
    void import('../PaymentSheetModal');
  }, [gated]);

  return useCallback(
    (request: PaymentSheetRequest) => {
      if (!request.patientId) return;
      // Destination semantics, matching the drawer it replaced: a second
      // trigger re-seeds the sheet rather than stacking another dialog.
      openAsDestination(MODAL_TYPES.PAYMENT_SHEET, request);
    },
    [openAsDestination],
  );
}

/**
 * Open the eligibility detail modal (gdn-2957).
 *
 * Destination semantics, like the payment sheet: a second trigger re-seeds
 * rather than stacking another copy.
 */
export function useOpenEligibilityDetail(): (
  request: EligibilityDetailRequest,
) => void {
  const { openAsDestination } = useModal();
  const gated = useFeatureGate(FeatureGates.PATIENT_BALANCE_TABLE);

  // Same reasoning as the payment sheet: warm the chunk so the dialog
  // doesn't open as a loading card first.
  useEffect(() => {
    if (!gated) return;
    void import('../EligibilityDetailModal');
  }, [gated]);

  return useCallback(
    (request: EligibilityDetailRequest) => {
      if (!request.patientId) return;
      openAsDestination(MODAL_TYPES.ELIGIBILITY_DETAIL, request);
    },
    [openAsDestination],
  );
}
